import { useRef, type ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import { Notice } from './Notice';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /**
   * Say what will actually happen, naming the record. "Are you sure?" tells the
   * user nothing they don't already know.
   */
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything destructive. */
  tone?: 'danger' | 'primary';
  loading?: boolean;
  /** Shown above the buttons when the action itself failed. */
  error?: ReactNode;
  /**
   * Holds the confirm button down when pressing it again cannot do anything
   * different — a refusal that will repeat identically until somebody edits
   * another record, say. **Only ever set it beside an `error` that says why**:
   * a bare disabled button makes a refusal, a permission denial and an ordinary
   * state look the same. Never a "nothing to send" guess; the house rule is
   * that an unnameable disabled state is a bug.
   */
  confirmDisabled?: boolean;
  /**
   * The confirm button's text while `loading`. Omitted, a known leading verb
   * becomes its progressive form ("Delete invoice" → "Deleting…", "Revoke key"
   * → "Revoking…"); an unknown verb keeps `confirmLabel` beside the spinner.
   */
  busyLabel?: string;
}

/*
 * Leading verb → what the button says while the request runs. A table, not a
 * suffix rule: English "-ing" spelling is irregular enough (void → voiding,
 * remove → removing, unsplit → unsplitting) that a rule would ship a typo.
 */
const BUSY_VERBS: Record<string, string> = {
  delete: 'Deleting',
  remove: 'Removing',
  revoke: 'Revoking',
  void: 'Voiding',
  archive: 'Archiving',
  retire: 'Retiring',
  restore: 'Restoring',
  unlink: 'Unlinking',
  disconnect: 'Disconnecting',
  reject: 'Rejecting',
  approve: 'Approving',
  discard: 'Discarding',
  overwrite: 'Overwriting',
  reset: 'Resetting',
  deny: 'Denying',
  unsplit: 'Unsplitting',
  recompute: 'Recomputing',
};

function busyLabelOf(confirmLabel: string): string {
  const verb = confirmLabel.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  const gerund = BUSY_VERBS[verb];
  return gerund ? `${gerund}…` : confirmLabel;
}

/**
 * Confirmation for an action the user cannot undo.
 *
 * Deliberately plain: the title is the question naming the record, the body is
 * one consequence sentence, and the only colour is the confirm button. No hero
 * icon, no tinted tile — the red button is the signal, and a warning glyph on
 * every dialog stops being read after the first one.
 *
 * Focus starts on Cancel, not Confirm: for a destructive action the safe option
 * is the one a stray Enter or Space hits. The backdrop is inert, so a mis-click
 * cannot dismiss the decision, and while the request runs Escape and × do
 * nothing either — closing mid-request would hide the result, including an
 * error that needs reading.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  tone = 'danger',
  loading = false,
  error,
  confirmDisabled = false,
  busyLabel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const close = () => {
    if (!loading) onClose();
  };
  // A second activation before the parent's `loading` lands would send the
  // request twice; the disabled attribute alone cannot catch that frame.
  const confirm = () => {
    if (!loading && !confirmDisabled) onConfirm();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      size="sm"
      dismissOnBackdrop={false}
      initialFocusRef={cancelRef}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={close} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger-solid' : 'primary'}
            onClick={confirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {loading ? (busyLabel ?? busyLabelOf(confirmLabel)) : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-fg-secondary">{description}</div>

      {/* A div, not a p: error is a ReactNode that can hold block elements (a
          sentence, a link, next steps) — nesting those in a p is invalid
          markup and throws a console error on an otherwise-fine-looking dialog. */}
      {error && (
        <Notice tone="danger" role="alert" className="mt-3">
          {error}
        </Notice>
      )}
    </Modal>
  );
}
